import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { Calendar as CalendarIcon } from "lucide-react";
import Calendar from "react-calendar";
import ClickAwayListener from "@mui/material/ClickAwayListener";

import { cn } from "@/utils/css";
import { TranslatedText } from "@/components/common/text";
import { useQuery } from "@/utils/query";
import { setDeliveryDate, setDeliveryDateEnd, setPickupDate, setPickupDateEnd } from "@/state/searchInputSlice";
import { today } from "@/utils/date";
import { setCurrentSearch } from "@/state/currentSearchSlice";
import { useLocation } from "react-router";

export const CalendarInput = ({ name, isLastInput = false, className }) => {
    const dispatch = useDispatch();
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const {
        pickupDate: queryPickupDate,
        pickupDateEnd: queryPickupDateEnd,
        deliveryDate: queryDeliveryDate,
        deliveryDateEnd: queryDeliveryDateEnd,
    } = useQuery();
    const { pickupDate, pickupDateEnd, deliveryDate, deliveryDateEnd, searchType } = useSelector((state) => state.searchInput);
    const isPickup = name === "pickup";
    const isLoadboard = searchType === "loadboard";
    const date = isPickup ? pickupDate : deliveryDate;
    const dateEnd = isPickup ? pickupDateEnd : deliveryDateEnd;
    const location = useLocation();

    const isToday = date === today;
    const isRange = dateEnd && date !== dateEnd;

    const formatDateRange = (startDate, endDate) => {
        const start = dayjs(startDate);
        const end = dayjs(endDate);

        // Check if same month and year
        if (start.month() === end.month() && start.year() === end.year()) {
            // Same month: "Jan 11 - 18 '25"
            return `${start.format("MMM D")} - ${end.format("D")} '${end.format("YY")}`;
        } else {
            // Different months: "Jan 11 - Feb 18 '25"
            return `${start.format("MMM D")} - ${end.format("MMM D")} '${end.format("YY")}`;
        }
    };

    const handleDateChange = (date, dateEnd = null) => {
        if (isPickup) {
            dispatch(setPickupDate(date));
            dispatch(setPickupDateEnd(dateEnd));
        } else {
            dispatch(setDeliveryDate(date));
            dispatch(setDeliveryDateEnd(dateEnd));
        }
    };

    const handleCalendarOpen = () => {
        setIsCalendarOpen(!isCalendarOpen);
    };

    const handleCalendarDates = (value) => {
        // Handle both single date and date range
        if (Array.isArray(value) && isLoadboard) {
            // Date range mode (only for loadboard)
            const [startDate, endDate] = value;
            const startFormatted = dayjs(startDate).format("YYYY-MM-DD");
            const endFormatted = endDate ? dayjs(endDate).format("YYYY-MM-DD") : null;

            handleDateChange(startFormatted, endFormatted);

            // Only close calendar if:
            // 1. We have both dates AND they're different (complete range)
            // 2. OR both dates are the same (user clicked same date twice = single date)
            if (endFormatted && startFormatted !== endFormatted) {
                // Complete range selected, close calendar
                setIsCalendarOpen(false);
            } else if (endFormatted && startFormatted === endFormatted) {
                // Same date selected twice, treat as single date
                handleDateChange(startFormatted, null);
                setIsCalendarOpen(false);
            }
            // Otherwise keep calendar open for second date selection
        } else {
            // Single date mode (for non-loadboard searches)
            const selectedDate = Array.isArray(value) ? value[0] : value;
            handleDateChange(dayjs(selectedDate).format("YYYY-MM-DD"), null);
            setIsCalendarOpen(false);
        }
    };

    useEffect(() => {
        if (isLoadboard) {
            handleDateChange(null);
        } else if (isPickup) {
            handleDateChange(today);
        }
    }, [isLoadboard]);

    useEffect(() => {
        const queryDate = isPickup ? queryPickupDate : queryDeliveryDate;
        const queryDateEnd = isPickup ? queryPickupDateEnd : queryDeliveryDateEnd;

        if (!queryDate && isLoadboard) {
            dispatch(
                setCurrentSearch(
                    isPickup
                        ? {
                              pickupDate: null,
                              pickupDateEnd: null,
                          }
                        : {
                              deliveryDate: null,
                              deliveryDateEnd: null,
                          },
                ),
            );
        } else {
            const dateToSet =
                queryDate && dayjs(queryDate).format("YYYY-MM-DD") ? dayjs(queryDate).format("YYYY-MM-DD") : isPickup ? today : null;
            const dateEndToSet = queryDateEnd && dayjs(queryDateEnd).format("YYYY-MM-DD") ? dayjs(queryDateEnd).format("YYYY-MM-DD") : null;
            dispatch(
                setCurrentSearch(
                    isPickup
                        ? {
                              pickupDate: dateToSet,
                              pickupDateEnd: dateEndToSet,
                          }
                        : {
                              deliveryDate: dateToSet,
                              deliveryDateEnd: dateEndToSet,
                          },
                ),
            );
            setTimeout(() => {
                //Needed to use setTimeout to avoid being set to null by changing searchType to loadboard
                handleDateChange(dateToSet, dateEndToSet);
            }, 0);
        }
        // Clearing done via SearchInputs clear
    }, [queryPickupDate, queryPickupDateEnd, queryDeliveryDate, queryDeliveryDateEnd, location.pathname]);

    return (
        <div className={cn("relative", className)}>
            <button
                className={cn(
                    "w-full h-[64px] md:w-[200px] inline-flex flex-col items-start justify-center bg-cm-p-surface-container px-4 py-[10px] mb-4 md:mb-0 rounded-b-lg md:rounded-bl-none md:rounded-r-lg",
                    {
                        "md:rounded-br-none": isCalendarOpen,
                        "md:mr-1 md:rounded-r-none": !isLastInput,
                    },
                )}
                type="button"
                onClick={handleCalendarOpen}
            >
                <TranslatedText className="text-sm text-cm-p-text-secondary mb-[2px]">{`${isPickup ? "Pickup" : "Delivery"} date`}</TranslatedText>
                <span className="inline-flex flex-row justify-between w-full items-center">
                    <span className="font-medium text-base">
                        {!date ? (
                            <TranslatedText>Any</TranslatedText>
                        ) : isRange ? (
                            formatDateRange(date, dateEnd)
                        ) : !isToday ? (
                            dayjs(date).format("MMM D 'YY")
                        ) : (
                            <TranslatedText>Today</TranslatedText>
                        )}
                    </span>
                    <span>
                        <CalendarIcon className="w-4 h-4 text-cm-p-text-secondary" />
                    </span>
                </span>
            </button>
            {isCalendarOpen && (
                <div
                    className={cn(
                        "absolute top-[69px] right-[-77px] bg-cm-p-surface w-[354px] rounded-xl z-50 drop-shadow-[0_2px_4px_rgba(33,33,33,0.12)]",
                        {
                            "right-[-73px]": !isLastInput,
                        },
                    )}
                >
                    <ClickAwayListener
                        onClickAway={() => {
                            setIsCalendarOpen(false);
                        }}
                    >
                        <div className="flex flex-col">
                            <Calendar
                                onChange={handleCalendarDates}
                                value={
                                    date && dateEnd && isLoadboard
                                        ? [dayjs(date).toDate(), dayjs(dateEnd).toDate()]
                                        : date
                                          ? dayjs(date).toDate()
                                          : undefined
                                }
                                className="cm-calendar"
                                locale="en-US"
                                maxDate={isPickup ? dayjs().add(7, "day").toDate() : undefined}
                                selectRange={isLoadboard}
                                allowPartialRange={isLoadboard}
                                formatShortWeekday={(locale, date) => date.toLocaleDateString(locale, { weekday: "short" }).slice(0, 2)}
                            />
                            <div className="flex w-full items-center p-4 pt-3 font-medium mt-3">
                                {isLoadboard ? (
                                    <button
                                        className="py-2 px-4 rounded-[58px] text-sm bg-cm-p-surface-container mr-3"
                                        type="button"
                                        onClick={() => {
                                            handleDateChange(null, null);
                                            setIsCalendarOpen(false);
                                        }}
                                    >
                                        <TranslatedText text="Any" />
                                    </button>
                                ) : null}
                                <button
                                    className="py-2 px-4 rounded-[58px] text-sm bg-cm-p-surface-container"
                                    type="button"
                                    onClick={() => {
                                        handleDateChange(today, null);
                                        setIsCalendarOpen(false);
                                    }}
                                >
                                    <TranslatedText text="Set today" />
                                </button>
                                <button
                                    className="py-2 px-4 rounded-lg text-sm bg-cm-p-accent-500 ml-auto min-w-[92px]"
                                    type="button"
                                    onClick={() => {
                                        setIsCalendarOpen(false);
                                    }}
                                >
                                    <TranslatedText text="Apply" />
                                </button>
                            </div>
                        </div>
                    </ClickAwayListener>
                </div>
            )}
        </div>
    );
};
